import React from 'react';
import { UserRoundX, Copy, BatteryLow } from 'lucide-react';
import { COBERTURA_MINIMA_SAUDAVEL_MESES, COBERTURA_MAXIMA_SAUDAVEL_MESES } from './coberturaSaldo';

interface AlertasBannerProps {
  titularidadeDivergenteCount: number;
  creditosDuplicadosCount: number;
  /** UCs com saldo de créditos abaixo de 1 mês de cobertura (alerta preventivo). */
  saldoBaixoCount?: number;
}

/**
 * Banners de alerta crítico (Seção 12 da spec): troca de titularidade e
 * créditos duplicados. As linhas afetadas na grade ficam destacadas em
 * laranja/vermelho (ver RateioTabelaUCs) — este card é só o resumo no topo.
 *
 * O terceiro banner (saldo abaixo da faixa saudável) é preventivo, não crítico:
 * fica por último, em âmbar, e a linha da grade NÃO é tingida — o destaque de
 * linha continua reservado aos dois alertas acima, que pedem ação. O sinal por
 * UC aparece na própria célula de saldo.
 */
const AlertasBanner: React.FC<AlertasBannerProps> = ({
  titularidadeDivergenteCount,
  creditosDuplicadosCount,
  saldoBaixoCount = 0,
}) => {
  if (titularidadeDivergenteCount === 0 && creditosDuplicadosCount === 0 && saldoBaixoCount === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      {titularidadeDivergenteCount > 0 && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900/40 text-orange-800 dark:text-orange-300 text-sm">
          <UserRoundX size={16} className="shrink-0" />
          <span>
            <strong>{titularidadeDivergenteCount}</strong> UC{titularidadeDivergenteCount !== 1 ? 's' : ''} com{' '}
            <strong>troca de titularidade detectada</strong> — CPF/CNPJ da última fatura diverge do cadastro.
          </span>
        </div>
      )}
      {creditosDuplicadosCount > 0 && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-800 dark:text-red-300 text-sm">
          <Copy size={16} className="shrink-0" />
          <span>
            <strong>{creditosDuplicadosCount}</strong> UC{creditosDuplicadosCount !== 1 ? 's' : ''} com{' '}
            <strong>créditos possivelmente duplicados</strong> — já constam num rateio protocolado/conciliado de outra UG
            neste mês.
          </span>
        </div>
      )}
      {saldoBaixoCount > 0 && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 text-amber-800 dark:text-amber-300 text-sm">
          <BatteryLow size={16} className="shrink-0" />
          <span>
            <strong>{saldoBaixoCount}</strong> UC{saldoBaixoCount !== 1 ? 's' : ''} com{' '}
            <strong>
              saldo de créditos abaixo de {COBERTURA_MINIMA_SAUDAVEL_MESES} mês de cobertura
            </strong>{' '}
            — a faixa saudável vai de {COBERTURA_MINIMA_SAUDAVEL_MESES} a {COBERTURA_MAXIMA_SAUDAVEL_MESES} meses. Veja
            a coluna Saldo Acumulado e avalie caso a caso; nada é ajustado automaticamente.
          </span>
        </div>
      )}
    </div>
  );
};

export default AlertasBanner;
